import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type PlayableInvoiceStatsRequestBody =
  paths['/playable-invoices/statistics']['post']['requestBody']['content']['application/json'];
export type PlayableInvoiceStatsResponse =
  paths['/playable-invoices/statistics']['post']['responses']['200']['content']['application/json'];
export type PlayableInvoiceStatsSearchFilters = PlayableInvoiceStatsRequestBody['search'];

export interface PlayableInvoiceStatsParams {
  makerOrganizationUuid: string;
  search?: PlayableInvoiceStatsSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchPlayableInvoiceStats = async ({
  makerOrganizationUuid,
  search,
  takerMarkets,
}: PlayableInvoiceStatsParams): Promise<PlayableInvoiceStatsResponse['data']> => {
  const response = await apiClient
    .post(`api/supplier-experience/playable-invoices/statistics`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((market) => ({
          marketUuid: market.marketUuid,
          supplierDivisionUuid: market.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies PlayableInvoiceStatsRequestBody,
    })
    .json<PlayableInvoiceStatsResponse>();

  return response.data;
};

const usePlayableInvoiceStats = (params: PlayableInvoiceStatsParams) => {
  const { makerOrganizationUuid, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['playable-invoice-stats', makerOrganizationUuid, search, ...takerMarketUuidsMap]
      : ['playable-invoice-stats-invalid'],
    queryFn: () => (isValid ? fetchPlayableInvoiceStats(params) : null),
    select: (data) => {
      const { totalAmount, totalCount, includedAmount, includedCount } = (data?.supplierMarkets ?? []).reduce(
        (acc, sm) => ({
          totalAmount: acc.totalAmount + sm.playable.amount,
          totalCount: acc.totalCount + sm.playable.count,
          includedAmount: acc.includedAmount + sm.offerable.amount,
          includedCount: acc.includedCount + sm.offerable.count,
        }),
        {
          totalAmount: 0,
          totalCount: 0,
          includedAmount: 0,
          includedCount: 0,
        }
      );
      const excludedAmount = totalAmount - includedAmount;
      const excludedCount = totalCount - includedCount;

      return {
        excludedAmount,
        excludedCount,
        includedAmount,
        includedCount,
        totalAmount,
        totalCount,
      };
    },
    staleTime: 0,
    gcTime: 0,
  });
};

export default usePlayableInvoiceStats;
